/**
 * The Officer Portal's conversations, over gov-api's `/chat` routes.
 *
 * Reads and writes only. Keeping the cache *live* is the realtime provider's
 * job (`realtime.tsx`), which writes straight into these query keys when the
 * socket pushes — so the keys are exported rather than kept private.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AttachmentDto } from '@munifocus/contracts-common';
import {
  upsertMessage,
  type ChatConversationPage,
  type ChatConversationView,
  type ChatMessagePage,
  type ChatMessageView,
  type MessagePages,
} from '@munifocus/chat';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

export interface ChatDirectoryEntry {
  userId: string;
  displayName: string;
  email: string;
  localGovernmentIds: string[];
  /** What they are at the municipalities you share — role keys, not labels. */
  roles: string[];
}

export const chatKeys = {
  conversations: ['chat', 'conversations'] as const,
  directory: ['chat', 'directory'] as const,
  messages: (conversationId: string) => ['chat', 'messages', conversationId] as const,
  /** Every thread's history at once — the prefix of {@link chatKeys.messages}. */
  allMessages: ['chat', 'messages'] as const,
};

export function useConversations() {
  return useQuery({
    queryKey: chatKeys.conversations,
    queryFn: async () => (await api.get<ChatConversationPage>('/chat/conversations')).data,
    // A slow backstop, not the delivery mechanism — the socket pushes every
    // change. This only rescues a client whose socket died without reconnecting,
    // so it is deliberately far longer than the notification bell's minute.
    refetchInterval: 5 * 60_000,
  });
}

export function useDirectory(enabled = true) {
  return useQuery({
    queryKey: chatKeys.directory,
    queryFn: async () => (await api.get<ChatDirectoryEntry[]>('/chat/directory')).data,
    // Only fetched when the "new conversation" picker actually opens: the roster
    // is irrelevant until then and does not change during a shift.
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * A thread's history, newest first, paged backwards on the server's cursor.
 *
 * Infinite rather than a single fetch: the first page is the latest fifty, and
 * a long file thread used to stop there with no way to reach anything older.
 * `fetchNextPage` pulls the page before the oldest one held.
 */
export function useMessages(conversationId: string | null) {
  return useInfiniteQuery({
    queryKey: chatKeys.messages(conversationId ?? ''),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      (
        await api.get<ChatMessagePage>(`/chat/conversations/${conversationId}/messages`, {
          params: pageParam ? { cursor: pageParam } : {},
        })
      ).data,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(conversationId),
  });
}

export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    // The panel reports a failed send and gives the text back to the composer.
    meta: { silent: true },
    mutationFn: async (input: {
      body: string;
      clientKey: string;
      attachments?: AttachmentDto[];
      mentionUserIds?: string[];
    }) =>
      (await api.post<ChatMessageView>(`/chat/conversations/${conversationId}/messages`, input))
        .data,
    // Merged immediately rather than waiting for our own broadcast to return:
    // the sender should see their message the moment it lands. `upsertMessage`
    // dedupes by id, so the socket echo that follows is a no-op.
    onSuccess: (message) => {
      queryClient.setQueryData<MessagePages>(chatKeys.messages(conversationId), (prev) =>
        upsertMessage(prev, message),
      );
      queryClient.invalidateQueries({ queryKey: chatKeys.conversations });
    },
  });
}

/**
 * Uploads one file and returns the descriptor the send call will quote.
 *
 * Separate from the send so a slow upload does not hold the message open with
 * nothing on screen, and a retry does not re-upload bytes already stored. The
 * server refuses any storage key that was not minted for this conversation, so
 * the descriptor is safe to round-trip through the client.
 */
export function useUploadAttachment(conversationId: string) {
  return useMutation({
    // A failed upload is shown on its own row in the composer.
    meta: { silent: true },
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post<AttachmentDto>(
        `/chat/conversations/${conversationId}/attachments`,
        form,
      );
      return data;
    },
  });
}

export function useMarkConversationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    // Background bookkeeping; a toast for a missed read receipt would be noise.
    meta: { silent: true },
    mutationFn: async (conversationId: string) => {
      await api.post(`/chat/conversations/${conversationId}/read`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chatKeys.conversations }),
  });
}

export function useStartDirect() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silent: true }, // the dialog shows its own error
    mutationFn: async (userId: string) =>
      (await api.post<ChatConversationView>('/chat/conversations/direct', { userId })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chatKeys.conversations }),
  });
}

export function useStartGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { silent: true }, // the dialog shows its own error
    mutationFn: async (input: { title: string; memberUserIds: string[] }) =>
      (await api.post<ChatConversationView>('/chat/conversations/group', input)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chatKeys.conversations }),
  });
}

export function useOpenFileThread() {
  const queryClient = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (fileId: string) =>
      (await api.post<ChatConversationView>(`/chat/conversations/file/${fileId}`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: chatKeys.conversations }),
    // The button re-enabled and nothing else happened.
    onError: (err) => toast.error(err, 'common.chat.startFailed'),
  });
}
